import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'

const PricingCard = ({
  id,
  name,
  subtitle,
  price,
  priceLabel,
  unitLabel,
  badge,
  disabled,
  billing,
  billedText,
  founding,
}) => {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()

  const subStatus = user?.subscription?.status
  const isPaid = subStatus === 'active' || subStatus === 'past_due'
  const isCurrentPlan = isPaid && user?.subscription?.tier === id

  const ctaLabel = !isAuthenticated
    ? 'Start Free Trial'
    : isCurrentPlan
      ? 'Current Plan'
      : isPaid
        ? 'Change Plan'
        : 'Purchase'

  const ctaDisabled = disabled || isCurrentPlan

  const handleClick = () => {
    if (ctaDisabled) return
    const query = `plan=${id}&billing=${billing || 'monthly'}${founding ? '&founding=1' : ''}`
    if (!isAuthenticated) {
      navigate(`/signup?next=${encodeURIComponent(`/payment?${query}`)}`)
      return
    }
    navigate(`/payment?${query}`)
  }

  // M2.1: "BEST VALUE" gets its own darker green (#2F7F0D) so it reads against the card
  // instead of matching the brand primary everywhere else on the page.
  const badgeBg =
    badge === 'BEST VALUE'
      ? 'bg-[#2F7F0D] text-white'
      : 'bg-brand-primary/90 text-white'

  return (
    // D2.1.1-new: the whole card is the target, not only the button. `handleClick` already
    // returns early when the card is disabled, so Ultimate stays inert without a second guard
    // here — what it does need is to not offer the pointer cursor, or it advertises an action
    // it will not perform. The CTA keeps its own handler for keyboard users and stops the
    // click there, otherwise a click on the button would fire this one too and navigate twice.
    <div
      onClick={handleClick}
      className={`
        relative
        w-[282px]
        h-[282px]
        rounded-[16px]
        overflow-hidden
        transition-transform duration-300
        ${disabled ? '' : 'hover:-translate-y-1'}
        ${ctaDisabled ? '' : 'cursor-pointer'}
      `}
      style={{ backgroundColor: '#17270D' }}
    >
      {/* radial glow top-right */}
      <div className="absolute inset-0 opacity-80 bg-[radial-gradient(circle_at_top_right,_rgba(76,188,24,0.55),_transparent_50%)]" />

      {/* inner rectangle 258×258 radius 12 */}
      <div className="absolute top-3 left-3 w-[258px] h-[258px] rounded-[12px] border border-white/[0.06]" />

      {/* small ellipse decoration */}
      <div className="absolute top-6 right-6 w-[52px] h-[52px] rounded-full bg-white/5" />

      {/* badge top-right */}
      {badge && (
        <div className={`absolute top-4 right-4 h-[22px] px-2 rounded-[4px] flex items-center ${badgeBg}`}>
          <span className="text-[12px] font-bold tracking-wide leading-[10px]">{badge}</span>
        </div>
      )}

      <div className="relative z-10 h-full p-6 flex flex-col text-left">
        <div className="text-left">
          <h3 className="text-[20px] leading-[32px] font-bold text-white text-left">{name}</h3>
          {subtitle && (
            <p className="text-[14px] leading-[20px] text-white/70 mt-1 text-left">{subtitle}</p>
          )}
        </div>

        <div className="mt-6 flex items-end gap-1">
          {price != null ? (
            <>
              <span className="text-[24px] font-bold text-white leading-none pb-2">$</span>
              <span className="text-[48px] font-extrabold text-white leading-none tracking-[-1px]">
                {price}
              </span>
              <span className="text-[20px] font-normal text-white/80 leading-[28px] pb-1 ml-1">
                /month
              </span>
            </>
          ) : (
            <>
              <span className="text-[24px] font-bold text-white leading-none pb-2">$</span>
              <span className="text-[48px] font-extrabold text-white leading-none">
                {/* M2.3: the short dash he asked for, not the em dash. */}
                {priceLabel ?? '-'}
              </span>
              <span className="text-[20px] font-normal text-white/80 leading-[28px] pb-1 ml-2">
                {unitLabel ?? ''}
              </span>
            </>
          )}
        </div>

        {billedText && (
          <p className="mt-2 text-[12px] leading-[16px] text-white/70 text-left">{billedText}</p>
        )}

        <div className="flex-1" />

        <button
          disabled={ctaDisabled}
          onClick={(e) => { e.stopPropagation(); handleClick() }}
          className={`
            w-full h-[56px] rounded-full text-[14px] font-semibold transition-all duration-200
            ${
              ctaDisabled
                ? 'bg-white/10 text-white/40 cursor-not-allowed'
                : 'bg-white text-brand-dark hover:bg-brand-pageFill'
            }
          `}
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  )
}

export default PricingCard
